import { JustSpinner, LocalSpinner } from 'components/Loading/Loading'
import React from 'react'
import styled from 'styled-components'
import { EmptyList, EmptyListProps } from './EmptyList'

export enum Align {
  LEFT = 'left',
  RIGHT = 'right',
  CENTER = 'center',
}

export interface OptionsProps<T> {
  header: string
  cell: (item: T) => React.ReactNode
  align?: Align
  id?: keyof T
  onClick?: (item: T) => void
}

interface TableProps<T> {
  options: OptionsProps<T>[]
  // item must have an id
  items: T[]
  cellKey?: keyof T
  emptyListOptions?: EmptyListProps
  loading?: boolean
  onClick?: (item: T) => void
}

export const Table = <T,>({
  options,
  items,
  cellKey,
  emptyListOptions,
  loading,
  onClick,
}: TableProps<T>) => {
  return (
    <TableWrapper>
      <HeaderBar />
      <TableStyled>
        <thead>
          <tr>
            {options.map((option) => (
              <th key={option.header} align={option.align || Align.LEFT}>
                {option.header}
              </th>
            ))}
          </tr>
        </thead>
        {loading ? (
          <tr>
            <td colSpan={options.length}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '400px',
                }}
              >
                <JustSpinner />
              </div>
            </td>
          </tr>
        ) : items.length > 0 ? (
          <tbody>
            {items.map((item) => (
              <tr key={item[cellKey] as string} onClick={() => onClick?.(item)}>
                {options.map((option) => (
                  <td key={option.header} align={option.align || Align.LEFT}>
                    {option.cell(item)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        ) : (
          <tr>
            <td colSpan={options.length}>
              <EmptyList {...emptyListOptions} />
            </td>
          </tr>
        )}
      </TableStyled>
    </TableWrapper>
  )
}

const TableStyled = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 0.75rem;

  z-index: 1;
  color: ${(props) => props.theme.textColor};

  thead tr {
    height: 40px;
  }
  thead {
    transform: translateY(-0.75rem);
  }

  th,
  td {
    padding: 0.5rem 1rem;
  }
  /* all except first row */
  tbody tr {
    position: relative;
  }

  tbody tr {
    position: relative;
    transform: translateY(-0.75rem);
    cursor: pointer;
  }

  tbody tr::after {
    content: '';
    position: absolute;
    inset: 0;
    background-color: ${(props) => props.theme.tableHeaderColor};
    border-radius: 12px;
    z-index: -1;
    border: 1px solid ${(props) => props.theme.tableRowBorder};
  }

  tbody tr:hover::after {
    border: 1px solid ${(props) => props.theme.colorOrange};
  }
`

const TableWrapper = styled.div`
  padding: 0 1rem;
  border-radius: 10px;
  overflow: hidden;
  ${(props) => props.theme.content};
`

const HeaderBar = styled.div`
  background-color: ${(props) => props.theme.tableHeaderColor};
  height: 40px;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: -1;
`
